import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import styled, { css } from 'styled-components';
import useSWRImmutable from 'swr/immutable';

import { formatDate } from 'shared/utils/formatDate';
import { formatString } from 'productTemplate/utils/formatStringForUrl';
import { getProductRobotImages } from 'productTemplate/utils/getProductRobotImages';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { IConsumerSearchFactsWindowData } from 'productTemplate/types/FactsWindowData';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setInitialCategoryWindowPosition } from 'store/slice/pdpStateSlice';
import { t } from 'localization/utils/t';
import { useBoundingClientRect } from 'shared/hooks/useBoundingClientRect';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import {
  WindowItemGrid,
  WindowItemLongText,
  WindowItemShortText,
  WindowItemFeaturedIn,
  Window,
} from 'shared/components/Window';

const TP = 'productTemplate.components.windows.FactsWindow';

const CategoryDetailsWindow = dynamic(
  () => import('productTemplate/components/windows/CategoryDetailsWindow'),
);

const TopProductsWindow = dynamic(
  () => import('productTemplate/components/windows/TopProductsWindow'),
);

interface IFactsWindow {
  className?: string;
  factsWindowData?: IConsumerSearchFactsWindowData;
  initialWindowPosition?: { bottom: number; left: number };
  isCrawler?: boolean;
  isPermanent?: boolean;
  onClick?: () => void;
  onClose: () => void;
}

const fetchData = (url) => requestService({ url, method: 'GET' });

const FactsWindow = ({
  className,
  initialWindowPosition,
  isCrawler = false,
  isPermanent,
  onClick,
  onClose,
}: IFactsWindow) => {
  const {
    brandName,
    category,
    color,
    designer,
    details,
    featuredIn: customFeaturedInLinks,
    id: productTemplateId,
    midsole,
    name,
    nickname,
    productType,
    releaseDate,
    robotAssets,
    season,
    silhouette,
    sku,
    slug,
    story,
    upperMaterial,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const { featuredInCollections } = useStoreSelector((state) => state.pdp);
  const releaseDateFull = formatDate.monthDayYearKebab(releaseDate);
  const releaseDateMonthYear = formatDate.monthYear(releaseDate);
  const releaseDateMonthYearEN = formatDate.monthYearEN(releaseDate);
  const isSneakers = productType === 'sneakers';
  const { brandProducts, categoryProducts, silhouetteProducts, isCategoryAndFacetMatching } =
    useStoreSelector((state) => state.pdp.factsWindowData);
  const robotImages = isCrawler ? getProductRobotImages({ robotAssets }) : null;
  // So that crawlerRobotImages is of the same type that SearchResultImageGrid takes
  const crawlerRobotImages = robotImages
    ?.map((robot) => {
      return { image_url: robot.url };
    })
    ?.map((robot) => {
      return {
        data: robot,
        value: t(`${TP}.crawlerRobotImagesAlt`, 'Model wearing {name}', { name }),
      };
    });

  const [isDesignerWindowOpen, setIsDesignerWindowOpen] = useState(false);
  const [isColorWindowOpen, setIsColorWindowOpen] = useState(false);
  const [isUpperMaterialWindowOpen, setIsUpperMaterialWindowOpen] = useState(false);
  const [isTechnologyWindowOpen, setIsTechnologyWindowOpen] = useState(false);
  const [isReleaseDateWindowOpen, setIsReleaseDateWindowOpen] = useState(false);
  const [isCategoryWindowOpen, setIsCategoryWindowOpen] = useState(false);
  const [isSeasonWindowOpen, setIsSeasonWindowOpen] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [isCategoryAndFacetAMatch, setIsCategoryAndFacetAMatch] = useState(
    isCategoryAndFacetMatching,
  );

  const showCategory = isSneakers && brandName && category.length > 0;
  const modalRef = useRef<HTMLDivElement>(null);
  const { left } = useBoundingClientRect(modalRef, isPermanent);
  const storeDispatch = useStoreDispatch();
  const { isDesktop, isMobile } = useWindowSize();
  const PDP_DESKTOP_FOOTER_HEIGHT = 75;
  const windowInnerHeight = !isCrawler
    ? document?.querySelector('#main-page-layout')?.clientHeight
    : 0;
  const mobileBuyBarHeight =
    isMobile && !isCrawler ? document?.querySelector('#buy-bar-mobile')?.clientHeight : 0;
  const mobileMarginBottom = (windowInnerHeight - 500) / 2 + mobileBuyBarHeight;

  const { data } = useSWRImmutable(
    `/web-api/v1/product_templates/recommended?productTemplateId=${productTemplateId}&count=8`,
    fetchData,
  );

  useEffect(() => {
    if (data?.productTemplates) {
      setRecommendedProducts(data.productTemplates);
    }
  }, [data]);

  useEffect(() => {
    setIsCategoryAndFacetAMatch(isCategoryAndFacetMatching);
    sendTrackingEvent('MODAL_OPEN_VIEW', {
      product_template_slug: slug,
      modal_name: 'facts',
    });
  }, [slug]);

  useEffect(() => {
    return () => {
      setIsDesignerWindowOpen(false);
      setIsColorWindowOpen(false);
      setIsUpperMaterialWindowOpen(false);
      setIsTechnologyWindowOpen(false);
      setIsReleaseDateWindowOpen(false);
      setIsCategoryWindowOpen(false);
      setIsSeasonWindowOpen(false);
    };
  }, []);

  const handleWheel = (e) => {
    e.stopPropagation();
  };

  const handleTextClick = (e, title, isOpen, fn) => {
    if (isDesktop && !isOpen) {
      const { top, left: leftEdge } = e.target.closest('.window-item').getBoundingClientRect();
      const windowHeight = window.innerHeight - PDP_DESKTOP_FOOTER_HEIGHT;
      storeDispatch(
        setInitialCategoryWindowPosition({
          title,
          position: { bottom: windowHeight - top, left: leftEdge },
        }),
      );
    }
    fn(true);
  };

  return (
    <>
      <FactsWindow.Window
        className={className}
        data-qa="facts_window"
        id="facts"
        initialPermanentPosition={initialWindowPosition || { left, bottom: 0 }}
        $isCrawler={isCrawler}
        $mobileMarginBottom={Math.ceil(mobileMarginBottom)}
        isCrawler={isCrawler}
        isPermanent={isPermanent}
        modalRef={modalRef}
        onClick={onClick}
        onClose={onClose}
        onWheel={handleWheel}
        title={t(`${TP}.facts`, 'Facts')}
      >
        <FactsWindow.Wrapper>
          {story && <WindowItemLongText text={story} />}
          {crawlerRobotImages && (
            <WindowItemGrid
              imageSize="90px"
              isClickable={false}
              isScrollable
              items={crawlerRobotImages}
              productTemplateSlug={slug}
              section="robot_images"
            />
          )}
          {brandName && (
            <WindowItemGrid
              headerLink={`/brand/${formatString(brandName)}`}
              imageSize="90px"
              isScrollable
              items={brandProducts}
              title={brandName}
              isCrawler={isCrawler}
              productTemplateSlug={slug}
              section="brand_name"
            />
          )}
          {silhouette && isSneakers && (
            <WindowItemGrid
              headerLink={`/sneakers/silhouette/${formatString(silhouette)}`}
              imageSize="90px"
              isScrollable
              items={silhouetteProducts}
              title={silhouette}
              isCrawler={isCrawler}
              productTemplateSlug={slug}
              section="sku_name"
            />
          )}
          {productType && !isSneakers && (
            <WindowItemGrid
              imageSize="90px"
              isScrollable
              items={categoryProducts}
              title={productType.replace(/_/g, ' ')}
              isCrawler={isCrawler}
              productTemplateSlug={slug}
              section="category"
            />
          )}
          {season && (
            <WindowItemShortText
              onTextClick={(e) =>
                handleTextClick(e, 'Season', isSeasonWindowOpen, setIsSeasonWindowOpen)
              }
              text={season}
              title={t(`${TP}.season`, 'Season')}
            />
          )}
          {releaseDate && (
            <WindowItemShortText
              onTextClick={(e) =>
                handleTextClick(
                  e,
                  'Release Date',
                  isReleaseDateWindowOpen,
                  setIsReleaseDateWindowOpen,
                )
              }
              text={releaseDateFull}
              title={t(`${TP}.releaseDate`, 'Release Date')}
            />
          )}
          {sku && <WindowItemShortText text={sku} title={t(`${TP}.sku`, 'SKU')} />}
          {designer && isSneakers && (
            <WindowItemShortText
              link={`/sneakers/designer/${formatString(designer)}`}
              onTextClick={(e) =>
                handleTextClick(e, 'Designer', isDesignerWindowOpen, setIsDesignerWindowOpen)
              }
              showLink={isCrawler}
              text={designer}
              title={t(`${TP}.designer`, 'Designer')}
            />
          )}
          {nickname && isSneakers && (
            <WindowItemShortText text={nickname} title={t(`${TP}.nickname`, 'Nickname')} />
          )}
          {details && (
            <WindowItemShortText text={details} title={t(`${TP}.colorway`, 'Colorway')} />
          )}
          {color && (
            <WindowItemShortText
              link={`/${isSneakers ? 'sneakers' : 'apparel'}/color/${formatString(color)}`}
              onTextClick={(e) =>
                handleTextClick(e, 'Main Color', isColorWindowOpen, setIsColorWindowOpen)
              }
              showLink={isCrawler}
              text={color}
              title={t(`${TP}.mainColor`, 'Main Color')}
            />
          )}
          {upperMaterial && isSneakers && (
            <WindowItemShortText
              link={`/sneakers/upper-material/${formatString(upperMaterial)}`}
              onTextClick={(e) =>
                handleTextClick(
                  e,
                  'Upper Material',
                  isUpperMaterialWindowOpen,
                  setIsUpperMaterialWindowOpen,
                )
              }
              showLink={isCrawler}
              text={upperMaterial}
              title={t(`${TP}.upperMaterial`, 'Upper Material')}
            />
          )}
          {midsole && isSneakers && (
            <WindowItemShortText
              link={`/sneakers/technology/${formatString(midsole)}`}
              onTextClick={(e) =>
                handleTextClick(e, 'Technology', isTechnologyWindowOpen, setIsTechnologyWindowOpen)
              }
              showLink={isCrawler}
              text={midsole}
              title={t(`${TP}.technology`, 'Technology')}
            />
          )}
          {showCategory && isCategoryAndFacetAMatch && (
            <WindowItemShortText
              link={`/sneakers/brand/${formatString(brandName)}/${formatString(category[0])}`}
              onTextClick={(e) =>
                handleTextClick(e, 'Category', isCategoryWindowOpen, setIsCategoryWindowOpen)
              }
              showLink={isCrawler}
              text={category[0]}
              title={t(`${TP}.category`, 'Category')}
            />
          )}
          {recommendedProducts && (
            <WindowItemGrid
              isLoading={!recommendedProducts}
              isScrollable
              items={recommendedProducts}
              title={t(`${TP}.recommendations`, 'Recommendations')}
              recommended
              isCrawler={isCrawler}
              imageSize="90px"
              section="recommended"
              productTemplateSlug={slug}
            />
          )}
          {(featuredInCollections?.length > 0 || customFeaturedInLinks?.length > 0) && (
            <WindowItemFeaturedIn
              customLinks={customFeaturedInLinks}
              featuredInCollections={featuredInCollections}
              title={t(`${TP}.related`, 'Related')}
            />
          )}
        </FactsWindow.Wrapper>
      </FactsWindow.Window>
      {designer && isDesignerWindowOpen && (
        <CategoryDetailsWindow
          facet="designer"
          onClose={() => setIsDesignerWindowOpen(false)}
          subtitle={designer}
          title={t(`${TP}.designer`, 'Designer')}
          name="Designer"
        />
      )}
      {color && isColorWindowOpen && (
        <CategoryDetailsWindow
          facet="color"
          onClose={() => setIsColorWindowOpen(false)}
          subtitle={color}
          title={t(`${TP}.mainColor`, 'Main Color')}
          name="Main Color"
        />
      )}
      {upperMaterial && isSneakers && isUpperMaterialWindowOpen && (
        <CategoryDetailsWindow
          facet="upper_material"
          onClose={() => setIsUpperMaterialWindowOpen(false)}
          subtitle={upperMaterial}
          title={t(`${TP}.upperMaterial`, 'Upper Material')}
          name="Upper Material"
        />
      )}
      {midsole && isSneakers && isTechnologyWindowOpen && (
        <CategoryDetailsWindow
          facet="technology"
          onClose={() => setIsTechnologyWindowOpen(false)}
          subtitle={midsole}
          title={t(`${TP}.technology`, 'Technology')}
          name="Technology"
        />
      )}
      {category && isCategoryWindowOpen && (
        <CategoryDetailsWindow
          facet="web_groups"
          onClose={() => setIsCategoryWindowOpen(false)}
          subtitle={category[0]}
          title={t(`${TP}.category`, 'Category')}
          name="Category"
        />
      )}
      {releaseDate && isReleaseDateWindowOpen && (
        <TopProductsWindow
          isSneakers
          onClose={() => setIsReleaseDateWindowOpen(false)}
          searchFilters={{ release_date_queryable: releaseDateMonthYearEN }}
          subtitle={releaseDateMonthYear}
          title={t(`${TP}.releaseDate`, 'Release Date')}
          name="Release Date"
        />
      )}
      {season && !isSneakers && isSeasonWindowOpen && (
        <TopProductsWindow
          onClose={() => setIsSeasonWindowOpen(false)}
          subtitle={season}
          title={t(`${TP}.season`, 'Season')}
          name="Season"
        />
      )}
    </>
  );
};

FactsWindow.Window = styled(Window)<{ $isCrawler: boolean; $mobileMarginBottom: number }>`
  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    max-height: 500px;
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: ${({ $mobileMarginBottom }) => $mobileMarginBottom}px;
    max-width: ${({ theme }) => theme.breakpoints.small - 1}px;
    ${({ $isCrawler }) =>
      $isCrawler &&
      css`
        margin: 0;
        max-height: none;
      `}
  }
`;
// TODO: correct inner exported css, hidescrollbars will not work without a css`` statement wrapping
FactsWindow.Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  max-height: 500px;

  ${hideScrollbars}

  div:first-child {
    padding-top: 0;
  }

  div:last-child {
    border-bottom: none;
  }
`;

export default FactsWindow;
